import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Checkbox, Input, Select } from 'antd';
import React from 'react';
import { Button } from '../../../components/button';
import { ErrorAwareLabel } from '../../../components/error-aware-label';
import { LabeledField } from '../../../components/form-fields/labeled-field';
import type {
    ContestConsentChannel,
    ContestConsentRecipient,
} from '../../../stores/contest';
import type { ConsentOptionForm } from './form';
import style from './style.module.scss';

const RECIPIENTS: Record<ContestConsentRecipient, string> = {
    retailer: 'Retailer (Couche-Tard / Circle K)',
    sponsor: 'Sponsor',
};

const CHANNELS: Record<ContestConsentChannel, string> = {
    email: 'Email',
    sms: 'Text message',
};

const LANGUAGES = [{ lang: 'en' }, { lang: 'fr' }] as const;

type Label = ConsentOptionForm['labels'][number];

function labelIn(option: ConsentOptionForm, lang: 'en' | 'fr'): Label {
    return (
        option.labels.find((label) => label.lang === lang) ?? {
            lang,
            labelQc: '',
            labelCa: null,
        }
    );
}

interface ConsentOptionsProps {
    value: ConsentOptionForm[];
    onChange: (options: ConsentOptionForm[]) => void;
    /** True when the option at this index has an error. */
    hasError: (index: number) => boolean;
}

/**
 * The marketing checkboxes of the sign-up form, in form order. None is
 * offered unless added here, and each recipient and channel at most once.
 */
export const ConsentOptions = ({
    value,
    onChange,
    hasError,
}: ConsentOptionsProps) => {
    const replace = (index: number, option: ConsentOptionForm) =>
        onChange(value.map((current, i) => (i === index ? option : current)));

    const setLabel = (
        index: number,
        lang: 'en' | 'fr',
        patch: Partial<Label>
    ) => {
        const option = value[index];
        const labels = LANGUAGES.map(({ lang: each }) =>
            each === lang
                ? { ...labelIn(option, each), ...patch }
                : labelIn(option, each)
        );
        replace(index, { ...option, labels });
    };

    const add = () =>
        onChange([
            ...value,
            {
                recipient: 'sponsor',
                channel: 'email',
                labels: LANGUAGES.map(({ lang }) => ({
                    lang,
                    labelQc: '',
                    labelCa: null,
                })),
                sameForCircleK: true,
            },
        ]);

    return (
        <div className={style.consent_options}>
            {value.map((option, index) => (
                <div
                    key={index}
                    className={`${style.consent_option} ${
                        hasError(index) ? style.consent_option_error : ''
                    }`}
                >
                    <div className={style.row}>
                        <LabeledField
                            label="Sent by"
                            className={style.recipient_field}
                        >
                            <Select<ContestConsentRecipient>
                                value={option.recipient}
                                onChange={(recipient) =>
                                    replace(index, { ...option, recipient })
                                }
                                options={Object.entries(RECIPIENTS).map(
                                    ([recipient, label]) => ({
                                        value: recipient,
                                        label,
                                    })
                                )}
                            />
                        </LabeledField>
                        <LabeledField
                            label="Channel"
                            className={style.channel_field}
                        >
                            <Select<ContestConsentChannel>
                                value={option.channel}
                                onChange={(channel) =>
                                    replace(index, { ...option, channel })
                                }
                                options={Object.entries(CHANNELS).map(
                                    ([channel, label]) => ({
                                        value: channel,
                                        label,
                                    })
                                )}
                            />
                        </LabeledField>
                        <Button
                            variant="secondary-destructive"
                            icon={<DeleteOutlined />}
                            onClick={() =>
                                onChange(value.filter((_, i) => i !== index))
                            }
                        >
                            Remove
                        </Button>
                    </div>
                    {/* One wording per row, English on the left and French on the right. */}
                    <div className={style.input_row}>
                        {LANGUAGES.map(({ lang }) => (
                            <div key={lang} className={style.input_col}>
                                <ErrorAwareLabel text={`Label ${lang}`} />
                                <Input.TextArea
                                    autoSize
                                    maxLength={500}
                                    value={labelIn(option, lang).labelQc}
                                    onChange={(e) =>
                                        setLabel(index, lang, {
                                            labelQc: e.target.value,
                                        })
                                    }
                                />
                            </div>
                        ))}
                    </div>
                    <Checkbox
                        checked={option.sameForCircleK}
                        onChange={(e) =>
                            replace(index, {
                                ...option,
                                sameForCircleK: e.target.checked,
                            })
                        }
                    >
                        Same wording for Circle K
                    </Checkbox>
                    {!option.sameForCircleK && (
                        <div className={style.input_row}>
                            {LANGUAGES.map(({ lang }) => (
                                <div key={lang} className={style.input_col}>
                                    <ErrorAwareLabel
                                        text={`Circle K label ${lang}`}
                                    />
                                    <Input.TextArea
                                        autoSize
                                        maxLength={500}
                                        value={
                                            labelIn(option, lang).labelCa ?? ''
                                        }
                                        onChange={(e) =>
                                            setLabel(index, lang, {
                                                labelCa: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
            <Button variant="secondary" icon={<PlusOutlined />} onClick={add}>
                Add a checkbox
            </Button>
        </div>
    );
};
